import {
    Anchor,
    Button,
    Card,
    Group,
    PasswordInput,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { api } from '../api';
import { apiErrorMessage } from '../api/client';
import { t } from '../dictionary';
import { useAuth } from '../providers/auth-context';

export const LoginPage = () => {
    const { setPartner } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const form = useForm({
        initialValues: { email: '', password: '' },
        validate: {
            email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : 'Enter a valid email'),
            password: (v) => (v ? null : 'Enter your password'),
        },
    });

    const submit = async (values: typeof form.values) => {
        setLoading(true);

        try {
            const { data } = await api.login(values);

            if (data.emailVerified === false) {
                navigate(`/verify?email=${encodeURIComponent(values.email)}`);

                return;
            }

            setPartner(data.partner);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card padding='xl' shadow='sm'>
            <form onSubmit={form.onSubmit(submit)}>
                <Stack gap='md'>
                    <div>
                        <Title order={2}>{t.auth.loginTitle}</Title>
                        <Text c='dimmed' size='sm' mt={4}>
                            {t.auth.loginSubtitle}
                        </Text>
                    </div>

                    <TextInput
                        label={t.auth.email}
                        placeholder='you@example.com'
                        autoComplete='email'
                        {...form.getInputProps('email')}
                    />

                    <PasswordInput
                        label={t.auth.password}
                        autoComplete='current-password'
                        {...form.getInputProps('password')}
                    />

                    <Group justify='space-between' mt='xs'>
                        <Anchor component={Link} to='/forgot-password' size='sm'>
                            {t.auth.forgotPassword}
                        </Anchor>
                        <Button type='submit' loading={loading}>
                            {t.auth.login}
                        </Button>
                    </Group>

                    <Text size='sm' c='dimmed' ta='center' mt='xs'>
                        {t.auth.noAccount}{' '}
                        <Anchor component={Link} to='/register' fw={600}>
                            {t.auth.createAccount}
                        </Anchor>
                    </Text>
                </Stack>
            </form>
        </Card>
    );
};
